import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { sfqApi } from "../../../api/feasibility";

// Redux Toolkit serializes thrown errors down to { name, message, ... } and drops
// the axios `response`, so the backend's message is lost after `.unwrap()`.
// Extract it here and hand it back via rejectWithValue so components can show it.
const apiErrMsg = (err) =>
  err?.response?.data?.error?.message || err?.message || "요청 처리 중 오류가 발생했습니다.";

// ── Study thunks ──────────────────────────────────────────────────────────────
export const fetchSFQList = createAsyncThunk("feasibility/fetchList", async () => {
  const res = await sfqApi.list();
  return res.data.data;
});

export const fetchSFQDetail = createAsyncThunk("feasibility/fetchDetail", async (id) => {
  const res = await sfqApi.detail(id);
  return res.data.data;
});

// Study Code는 unique다. 중복이면 409와 함께 어느 스터디가 쓰고 있는지 서버가 알려주므로,
// 그 메시지를 살려서 모달이 그대로 띄울 수 있게 한다.
export const createSFQ = createAsyncThunk(
  "feasibility/create",
  async (body, { rejectWithValue }) => {
    try {
      const res = await sfqApi.create(body);
      return res.data.data;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// `body` is a patch — only the fields it carries are written server-side.
export const saveSFQInfo = createAsyncThunk(
  "feasibility/saveInfo",
  async ({ id, body }, { rejectWithValue }) => {
    try {
      // emailWarning: reviewer/owner/CRA 지정은 저장됐으나 당사자 알림 메일이 나가지 못한 경우.
      const res = await sfqApi.updateInfo(id, body);
      return { id, body, emailWarning: res.data?.data?.emailWarning };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const updateSFQStatus = createAsyncThunk(
  "feasibility/updateStatus",
  async ({ id, newStatus, reason }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.updateStatus(id, newStatus, reason);
      // Going Active snapshots a version server-side; versionNo comes back only then.
      // emailWarning: status changed but the PM notification email failed to send.
      return { id, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const saveNarrative = createAsyncThunk("feasibility/saveNarrative", async ({ id, html }) => {
  await sfqApi.updateNarrative(id, html);
  return { id, html };
});

export const saveSFQQuestions = createAsyncThunk(
  "feasibility/saveSFQQuestions",
  async ({ id, questions }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.saveSFQQuestions(id, questions);
      return res.data.data;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// ── Version thunks ────────────────────────────────────────────────────────────
// Publishing needs the study PM's approval, so it is a request, not an action:
// no version exists until approvePublish resolves.
export const requestPublish = createAsyncThunk(
  "feasibility/requestPublish",
  async (id, { rejectWithValue }) => {
    try {
      // emailWarning: request saved but the PM notification failed to send.
      const res = await sfqApi.requestPublish(id);
      return res.data.data;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const cancelPublishRequest = createAsyncThunk(
  "feasibility/cancelPublishRequest",
  async (id, { rejectWithValue }) => {
    try {
      await sfqApi.cancelPublishRequest(id);
      return true;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const approvePublish = createAsyncThunk(
  "feasibility/approvePublish",
  async (id, { rejectWithValue }) => {
    try {
      const res = await sfqApi.approvePublish(id);
      // emailWarning: version published but the requester notification failed to send.
      return res.data.data; // { versionId, versionNo }
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const rejectPublish = createAsyncThunk(
  "feasibility/rejectPublish",
  async ({ id, reason }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.rejectPublish(id, reason);
      return { reason, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const discardDraft = createAsyncThunk(
  "feasibility/discardDraft",
  async (id, { rejectWithValue }) => {
    try {
      await sfqApi.discardDraft(id);
      return true;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const fetchVersions = createAsyncThunk("feasibility/fetchVersions", async (id) => {
  const res = await sfqApi.getVersions(id);
  return res.data.data;
});

export const fetchVersion = createAsyncThunk(
  "feasibility/fetchVersion",
  async ({ id, versionId }) => {
    const res = await sfqApi.getVersion(id, versionId);
    return res.data.data;
  }
);

// The latest published version, shown in the Overview/Questions tabs while a
// publish request is pending — the working copy is then an unapproved change.
// Kept apart from `currentVersion`, which the Version History tab owns.
export const fetchPublishedVersion = createAsyncThunk(
  "feasibility/fetchPublishedVersion",
  async ({ id, versionId }) => {
    const res = await sfqApi.getVersion(id, versionId);
    return res.data.data;
  }
);

// ── Invitation thunks ─────────────────────────────────────────────────────────
export const fetchInvitations = createAsyncThunk("feasibility/fetchInvitations", async (sfqId) => {
  const res = await sfqApi.getInvitations(sfqId);
  return res.data.data;
});

export const addInvitation = createAsyncThunk(
  "feasibility/addInvitation",
  async ({ sfqId, data }, { rejectWithValue }) => {
    try {
      // The API validates required fields and column widths and names the offending
      // one in its 400, so the message is worth carrying back to the form.
      const res = await sfqApi.addInvitation(sfqId, data);
      return res.data.data;
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const updateInvitation = createAsyncThunk(
  "feasibility/updateInvitation",
  async ({ sfqId, invId, data }, { rejectWithValue }) => {
    try {
      // The API decides which fields its status allows and answers 422 on the rest,
      // so the error is worth surfacing rather than swallowing.
      const res = await sfqApi.updateInvitation(sfqId, invId, data);
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const extendInvExpiry = createAsyncThunk(
  "feasibility/extendInvExpiry",
  async ({ sfqId, invId, expiry }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.extendInvExpiry(sfqId, invId, expiry);
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const revokeInvitation = createAsyncThunk(
  "feasibility/revokeInvitation",
  async ({ sfqId, invId, reason }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.revokeInvitation(sfqId, invId, reason);
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const sendInvitation = createAsyncThunk(
  "feasibility/sendInvitation",
  async ({ sfqId, invId, subject, bodyHtml, lang, includeCrc }, { rejectWithValue }) => {
    try {
      // Backend sends the invitation email inside a transaction: on mail failure it
      // rolls back and responds with an error, so a resolved call means mail went out.
      // Response carries the version pinned to this invitation.
      const res = await sfqApi.sendInvitation(sfqId, invId, { subject, bodyHtml, lang, includeCrc });
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// 답변이 없는 사이트에 보내는 재촉 메일. sendInvitation과 달리 아무것도 바꾸지 않는다 —
// 비밀번호도, 만료일도, 상태도, 고정된 버전도 그대로다. 응답에도 그래서 갱신할 필드가 없고,
// 리듀서가 손대는 것은 발송 횟수와 시각뿐이다.
export const sendReminder = createAsyncThunk(
  "feasibility/sendReminder",
  async ({ sfqId, invId, subject, bodyHtml, lang, includeCrc }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.sendReminder(sfqId, invId, { subject, bodyHtml, lang, includeCrc });
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const fetchInvitationDetail = createAsyncThunk(
  "feasibility/fetchInvitationDetail",
  async ({ sfqId, invId }) => {
    const res = await sfqApi.getInvitationDetail(sfqId, invId);
    return res.data.data;
  }
);

export const updateInvStatus = createAsyncThunk(
  "feasibility/updateInvStatus",
  async ({ sfqId, invId, newStatus, reason }, { rejectWithValue }) => {
    try {
      const res = await sfqApi.updateInvStatus(sfqId, invId, newStatus, reason);
      return { invId, newStatus: res.data.data.newStatus };
    } catch (err) {
      // The server refuses a rollback without a reason, or while a risk override is
      // pending — the caller shows those messages rather than a generic failure.
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const requestRiskOverride = createAsyncThunk(
  "feasibility/requestRiskOverride",
  async ({ sfqId, invId, overrideRisk, reason }, { rejectWithValue }) => {
    try {
      // res.data.data may carry emailWarning: request saved but PM notification failed.
      const res = await sfqApi.requestRiskOverride(sfqId, invId, overrideRisk, reason);
      return { invId, overrideRisk, reason, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

export const approveRiskOverride = createAsyncThunk(
  "feasibility/approveRiskOverride",
  async ({ sfqId, invId }) => {
    await sfqApi.approveRiskOverride(sfqId, invId);
    return invId;
  }
);

// Withdraw (requester) or reject (owner) a pending override. `reason` is required only
// for the owner's rejection; the API is the one that decides which leg this is.
export const rejectRiskOverride = createAsyncThunk(
  "feasibility/rejectRiskOverride",
  async ({ sfqId, invId, reason }, { rejectWithValue }) => {
    try {
      // res.data.data may carry emailWarning: rejected but the requester was not told.
      const res = await sfqApi.rejectRiskOverride(sfqId, invId, reason);
      return { invId, ...res.data.data };
    } catch (err) {
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// ── CRA Assessment thunk ──────────────────────────────────────────────────────
export const saveCRAAssessment = createAsyncThunk(
  "feasibility/saveCRAAssessment",
  async ({ sfqId, invId, type, data }, { rejectWithValue }) => {
    try {
      await sfqApi.saveCRAAssessment(sfqId, invId, type, data);
      return { invId, type, data };
    } catch (err) {
      // 422 once the invitation is Reviewed or later — the assessment is frozen there.
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// ── CRA Comment thunk ────────────────────────────────────────────────────────
export const saveCRAComment = createAsyncThunk(
  "feasibility/saveCRAComment",
  async ({ sfqId, invId, questionKey, commentText }, { rejectWithValue }) => {
    try {
      await sfqApi.saveCRAComment(sfqId, invId, questionKey, commentText);
      return { invId, questionKey, commentText };
    } catch (err) {
      // 422 once the invitation is Reviewed or later — comments freeze with the review.
      return rejectWithValue({ message: apiErrMsg(err) });
    }
  }
);

// ── Audit Trail thunk ────────────────────────────────────────────────────────
export const fetchAuditTrail = createAsyncThunk("feasibility/fetchAuditTrail", async (id) => {
  const res = await sfqApi.getAuditTrail(id);
  return res.data.data;
});

// ── Template list thunks ──────────────────────────────────────────────────────
// Only the *lists* live in Redux — the picker and the managers fetch a template's
// body (html / questions) into local state when one is selected, since a stale
// cached body is worse than a second request.
export const fetchNarrativeTemplates = createAsyncThunk(
  "feasibility/fetchNarrativeTemplates",
  async () => {
    const res = await sfqApi.listNarrativeTemplates();
    return res.data.data;
  }
);

export const fetchQuestionTemplates = createAsyncThunk(
  "feasibility/fetchQuestionTemplates",
  async () => {
    const res = await sfqApi.listQuestionTemplates();
    return res.data.data;
  }
);

// ── Prior content thunk ───────────────────────────────────────────────────────
export const fetchPriorVersions = createAsyncThunk(
  "feasibility/fetchPriorVersions",
  async () => {
    const res = await sfqApi.listPriorVersions();
    return res.data.data;
  }
);

// ── Slice ─────────────────────────────────────────────────────────────────────
const feasibilitySlice = createSlice({
  name: "feasibility",
  initialState: {
    // Study list
    list: [],
    loading: false,
    // Current study
    currentSFQ: null,
    sfqQuestions: [],
    detailLoading: false,
    // Site invitations for current study
    invitations: [],
    invitationsLoading: false,
    // Currently viewed invitation
    currentInvitation: null,
    invDetailLoading: false,
    // Template lists (metadata only — bodies are fetched on selection)
    narrativeTemplates: [],
    ntLoading: false,
    questionTemplates: [],
    qtLoading: false,
    // Published versions of the user's own studies, pickable as a starting point
    priorVersions: [],
    priorVersionsLoading: false,
    auditTrail: [],
    // Version history for current study
    versions: [],
    versionsLoading: false,
    currentVersion: null,
    // Latest published version, loaded only while a publish request is pending.
    publishedVersion: null,
    publishedVersionLoading: false,
  },
  reducers: {
    clearCurrentSFQ(state) {
      state.currentSFQ    = null;
      state.sfqQuestions  = [];
      state.invitations   = [];
      state.currentInvitation = null;
      state.auditTrail    = [];
      state.versions      = [];
      state.currentVersion = null;
      state.publishedVersion = null;
    },
    clearCurrentVersion(state) {
      state.currentVersion = null;
    },
    clearCurrentInvitation(state) {
      state.currentInvitation = null;
    },
    patchInvitationComment(state, action) {
      const { invId, questionKey, commentText } = action.payload;
      if (state.currentInvitation?.id === invId) {
        const ans = state.currentInvitation.answers?.find(
          (a) => a.questionKey === questionKey
        );
        if (ans) ans.commentText = commentText;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Study list
      .addCase(fetchSFQList.pending,    (s) => { s.loading = true; })
      .addCase(fetchSFQList.fulfilled,  (s, a) => { s.loading = false; s.list = a.payload; })
      .addCase(fetchSFQList.rejected,   (s) => { s.loading = false; })

      // Study status change
      .addCase(updateSFQStatus.fulfilled, (s, a) => {
        const { id, newStatus, versionNo, sendBackComment, rolledBack } = a.payload;
        if (s.currentSFQ?.id === id) {
          s.currentSFQ.status = newStatus;
          // Approve (→ Active) snapshots a version: reflect the bump and clear dirty.
          if (versionNo != null) {
            s.currentSFQ.latestVersionNo = versionNo;
            s.currentSFQ.contentDirty = 0;
          }
          // Send Back records a reason; leaving Draft again makes it stale. A rollback
          // into Draft is not a rejection, so an older reason must not resurface with it
          // — StatusBar refetches right after, this just avoids the flash.
          if (rolledBack) s.currentSFQ.lastSendBackComment = null;
          else if (sendBackComment !== undefined) s.currentSFQ.lastSendBackComment = sendBackComment;
          else if (newStatus !== "Draft") s.currentSFQ.lastSendBackComment = null;
          // Same for the rollback reason, which the server derives from the newest
          // status audit row: any transition supersedes it. The response does not carry
          // the reason back, so the refetch StatusBar fires after a rollback is what
          // fills it in — clearing here only avoids showing the previous one meanwhile.
          s.currentSFQ.lastRollbackComment = null;
        }
        const item = s.list.find((f) => f.id === id);
        if (item) {
          item.status = newStatus;
          if (versionNo != null) item.latestVersionNo = versionNo;
        }
      })

      // Study detail
      .addCase(fetchSFQDetail.pending,  (s) => { s.detailLoading = true; })
      .addCase(fetchSFQDetail.fulfilled,(s, a) => {
        s.detailLoading = false;
        const { questions, ...sfq } = a.payload;
        s.currentSFQ   = sfq;
        s.sfqQuestions = questions || [];
      })
      .addCase(fetchSFQDetail.rejected, (s) => { s.detailLoading = false; })

      // Study info patch (studyCode / studyTitle / reviewerEmployeeNo / createdBy).
      // The CRA roster (craEmployeeNos → cras) and derived names come from the
      // fetchSFQDetail that every editor fires after saving, so only simple scalar
      // fields are merged optimistically here. Guard on id: a save can resolve after
      // the user switched to another SFQ.
      .addCase(saveSFQInfo.fulfilled, (s, a) => {
        const { id, body } = a.payload;
        if (s.currentSFQ?.id !== id) return;
        const { craEmployeeNos, ...scalar } = body;
        Object.assign(s.currentSFQ, scalar);
      })

      // Narrative edit → working copy is now dirty (unpublished changes)
      // Guard on id: a save can resolve after the user switched to another SFQ.
      .addCase(saveNarrative.fulfilled, (s, a) => {
        const { id, html } = a.payload;
        if (s.currentSFQ?.id !== id) return;
        s.currentSFQ.narrativeHtml = html;
        s.currentSFQ.contentDirty = 1;
      })

      // Save questions → working copy dirty
      .addCase(saveSFQQuestions.fulfilled, (s, a) => {
        if (a.payload) s.sfqQuestions = a.payload;
        if (s.currentSFQ) s.currentSFQ.contentDirty = 1;
      })

      // Publish requested → working copy frozen until the PM approves or rejects.
      // No version exists yet, so latestVersionNo and contentDirty are untouched.
      .addCase(requestPublish.fulfilled, (s) => {
        if (s.currentSFQ) {
          s.currentSFQ.publishPendingPM = 1;
          s.currentSFQ.lastPublishRejectComment = null; // a new request supersedes it
        }
      })

      .addCase(cancelPublishRequest.fulfilled, (s) => {
        if (s.currentSFQ) s.currentSFQ.publishPendingPM = 0;
        s.publishedVersion = null; // tabs go back to showing the working copy
      })

      // PM approved → the version is snapshotted now: dirty clears, version bumps.
      .addCase(approvePublish.fulfilled, (s, a) => {
        const versionNo = a.payload?.versionNo;
        if (s.currentSFQ) {
          s.currentSFQ.publishPendingPM = 0;
          s.currentSFQ.contentDirty = 0;
          if (versionNo != null) s.currentSFQ.latestVersionNo = versionNo;
        }
        // Keep the study list's Version column in sync without a refetch.
        const item = s.list.find((f) => f.id === a.meta.arg);
        if (item && versionNo != null) item.latestVersionNo = versionNo;
        // The approved working copy is now the published version.
        s.publishedVersion = null;
      })

      // PM rejected → working copy unfrozen and still dirty, so the CRA resumes
      // editing exactly where they left off.
      .addCase(rejectPublish.fulfilled, (s, a) => {
        if (s.currentSFQ) {
          s.currentSFQ.publishPendingPM = 0;
          s.currentSFQ.lastPublishRejectComment = a.payload.reason;
        }
        s.publishedVersion = null;
      })

      .addCase(fetchPublishedVersion.pending,   (s) => { s.publishedVersionLoading = true; })
      .addCase(fetchPublishedVersion.fulfilled, (s, a) => {
        s.publishedVersionLoading = false;
        s.publishedVersion = a.payload;
      })
      .addCase(fetchPublishedVersion.rejected,  (s) => { s.publishedVersionLoading = false; })

      // Discard draft → working copy reverted, no longer dirty
      // (component re-fetches detail to reload the restored content)
      .addCase(discardDraft.fulfilled, (s) => {
        if (s.currentSFQ) s.currentSFQ.contentDirty = 0;
      })

      // Version history
      .addCase(fetchVersions.pending,   (s) => { s.versionsLoading = true; })
      .addCase(fetchVersions.fulfilled, (s, a) => {
        s.versionsLoading = false;
        s.versions = a.payload || [];
      })
      .addCase(fetchVersions.rejected,  (s) => { s.versionsLoading = false; })
      .addCase(fetchVersion.fulfilled,  (s, a) => { s.currentVersion = a.payload; })

      // Invitations
      .addCase(fetchInvitations.pending,    (s) => { s.invitationsLoading = true; })
      .addCase(fetchInvitations.fulfilled,  (s, a) => {
        s.invitationsLoading = false;
        s.invitations = a.payload;
      })
      .addCase(fetchInvitations.rejected,   (s) => { s.invitationsLoading = false; })

      .addCase(addInvitation.fulfilled, (s, a) => {
        s.invitations = [...s.invitations, a.payload];
      })

      // The row the API wrote is authoritative — it applied only the fields its status
      // allows, so replacing the row is what keeps the grid honest about what stuck.
      .addCase(updateInvitation.fulfilled, (s, a) => {
        const { invId, invitation } = a.payload;
        if (!invitation) return;
        const idx = s.invitations.findIndex((i) => i.id === invId);
        if (idx !== -1) s.invitations[idx] = { ...s.invitations[idx], ...invitation };
        if (s.currentInvitation?.id === invId) {
          s.currentInvitation = { ...s.currentInvitation, ...invitation };
        }
      })

      // `reopened` is the API's own answer to whether the new date puts the link back in
      // the site's hands. It clears the revocation there, so the row has to follow —
      // otherwise the grid keeps saying 회수됨 about a link that opens.
      .addCase(extendInvExpiry.fulfilled, (s, a) => {
        const { invId, siteTokenExpiry, reopened } = a.payload;
        const apply = (inv) => {
          inv.siteTokenExpiry = siteTokenExpiry;
          if (reopened) { inv.revokedAt = null; inv.revokedByName = null; }
        };
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) apply(inv);
        if (s.currentInvitation?.id === invId) apply(s.currentInvitation);
      })

      .addCase(revokeInvitation.fulfilled, (s, a) => {
        const { invId, siteTokenExpiry, revokedAt, revokedByName } = a.payload;
        const apply = (inv) => {
          inv.siteTokenExpiry = siteTokenExpiry;
          inv.revokedAt       = revokedAt;
          inv.revokedByName   = revokedByName;
        };
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) apply(inv);
        if (s.currentInvitation?.id === invId) apply(s.currentInvitation);
      })

      .addCase(sendInvitation.fulfilled, (s, a) => {
        const { invId, sentVersionId, sentVersionNo, siteTokenExpiry, expiryRenewed } = a.payload;
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) {
          // Only a Pending invitation is promoted to Sent — a re-send to a site that has
          // already answered must not drag it back from Submitted/Reviewed. Mirrors the
          // CASE in markInvitationSent.
          if (inv.status === "Pending") inv.status = "Sent";
          inv.sentVersionId   = sentVersionId ?? inv.sentVersionId;
          inv.sentVersionNo   = sentVersionNo ?? inv.sentVersionNo;
          inv.emailSentCount  = (inv.emailSentCount || 0) + 1;
          inv.lastEmailSentAt = new Date().toISOString();
          // A send renews an expiry that had already passed, so the row's Expires column
          // has to follow the date the mail actually went out with.
          if (siteTokenExpiry) inv.siteTokenExpiry = siteTokenExpiry;
          // The other way a revoked link comes back. markInvitationSent clears the
          // revocation on the same branch that renews the expiry, so the row does too.
          if (expiryRenewed) { inv.revokedAt = null; inv.revokedByName = null; }
        }
        if (s.currentInvitation?.id === invId && siteTokenExpiry) {
          s.currentInvitation.siteTokenExpiry = siteTokenExpiry;
        }
        // First send of a study with no published version bootstraps V1 server-side.
        if (s.currentSFQ && s.currentSFQ.latestVersionNo == null && sentVersionNo != null) {
          s.currentSFQ.latestVersionNo = sentVersionNo;
        }
      })

      // 알림은 행의 상태를 바꾸지 않는다. 늘어나는 것은 "몇 통 나갔나"뿐이고, ✉ 배지와
      // 알림 칼럼의 "2/3"이 각각 그 두 숫자를 읽는다.
      .addCase(sendReminder.fulfilled, (s, a) => {
        const inv = s.invitations.find((i) => i.id === a.payload.invId);
        if (inv) {
          inv.emailSentCount    = (inv.emailSentCount || 0) + 1;
          inv.reminderSentCount = (inv.reminderSentCount || 0) + 1;
          inv.lastEmailSentAt   = new Date().toISOString();
        }
      })

      // Invitation detail
      .addCase(fetchInvitationDetail.pending,    (s) => { s.invDetailLoading = true; })
      .addCase(fetchInvitationDetail.fulfilled,  (s, a) => {
        s.invDetailLoading  = false;
        s.currentInvitation = a.payload;
      })
      .addCase(fetchInvitationDetail.rejected,   (s) => { s.invDetailLoading = false; })

      .addCase(updateInvStatus.fulfilled, (s, a) => {
        const { invId, newStatus } = a.payload;
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) inv.status = newStatus;
        if (s.currentInvitation?.id === invId) s.currentInvitation.status = newStatus;
      })

      .addCase(requestRiskOverride.fulfilled, (s, a) => {
        const { invId, overrideRisk, reason, pendingPM, requestedBy } = a.payload;
        if (s.currentInvitation?.id === invId) {
          s.currentInvitation.riskOverride           = overrideRisk;
          s.currentInvitation.riskOverrideReason     = reason;
          // The requester is who may withdraw it, so the screen needs it right away.
          s.currentInvitation.riskOverrideBy         = requestedBy ?? null;
          s.currentInvitation.riskOverridePendingPM  = pendingPM ? 1 : 0;
          s.currentInvitation.riskOverrideApprovedBy = null;
        }
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) inv.riskOverridePendingPM = pendingPM ? 1 : 0;
      })

      .addCase(approveRiskOverride.fulfilled, (s, a) => {
        const inv = s.invitations.find((i) => i.id === a.payload);
        if (inv) inv.riskOverridePendingPM = 0;
        if (s.currentInvitation?.id === a.payload) {
          s.currentInvitation.riskOverridePendingPM = 0;
        }
      })

      // Withdrawn or rejected — the request is gone either way, and the invitation is
      // back on the risk level its final assessment produced.
      .addCase(rejectRiskOverride.fulfilled, (s, a) => {
        const { invId } = a.payload;
        const inv = s.invitations.find((i) => i.id === invId);
        if (inv) inv.riskOverridePendingPM = 0;
        if (s.currentInvitation?.id === invId) {
          s.currentInvitation.riskOverride           = null;
          s.currentInvitation.riskOverrideReason     = null;
          s.currentInvitation.riskOverrideBy         = null;
          s.currentInvitation.riskOverridePendingPM  = 0;
          s.currentInvitation.riskOverrideApprovedBy = null;
        }
      })

      // Narrative templates
      .addCase(fetchNarrativeTemplates.pending,   (s) => { s.ntLoading = true; })
      .addCase(fetchNarrativeTemplates.fulfilled, (s, a) => {
        s.ntLoading = false;
        s.narrativeTemplates = a.payload || [];
      })
      .addCase(fetchNarrativeTemplates.rejected,  (s) => { s.ntLoading = false; })

      // Question templates
      .addCase(fetchQuestionTemplates.pending,    (s) => { s.qtLoading = true; })
      .addCase(fetchQuestionTemplates.fulfilled,  (s, a) => {
        s.qtLoading = false;
        s.questionTemplates = a.payload || [];
      })
      .addCase(fetchQuestionTemplates.rejected,   (s) => { s.qtLoading = false; })

      // Prior content
      .addCase(fetchPriorVersions.pending,    (s) => { s.priorVersionsLoading = true; })
      .addCase(fetchPriorVersions.fulfilled,  (s, a) => {
        s.priorVersionsLoading = false;
        s.priorVersions = a.payload || [];
      })
      .addCase(fetchPriorVersions.rejected,   (s) => { s.priorVersionsLoading = false; })

      // CRA Assessment — patch currentInvitation in-place
      .addCase(saveCRAAssessment.fulfilled, (s, a) => {
        const { invId, type, data } = a.payload;
        if (s.currentInvitation?.id === invId) {
          if (type === "initial") {
            s.currentInvitation.craInitialPiExp      = data.piExp     ?? s.currentInvitation.craInitialPiExp;
            s.currentInvitation.craInitialGcpStatus  = data.gcpStatus ?? s.currentInvitation.craInitialGcpStatus;
            s.currentInvitation.craInitialRegStatus  = data.regStatus ?? s.currentInvitation.craInitialRegStatus;
            s.currentInvitation.craInitialRisk       = data.risk      ?? s.currentInvitation.craInitialRisk;
            s.currentInvitation.craInitialRemarks    = data.remarks   ?? s.currentInvitation.craInitialRemarks;
          } else {
            s.currentInvitation.craFinalOpinion        = data.opinion        ?? s.currentInvitation.craFinalOpinion;
            s.currentInvitation.craFinalBasisConfirmed = data.basisConfirmed ?? s.currentInvitation.craFinalBasisConfirmed;
            s.currentInvitation.craFinalRecommendation = data.recommendation ?? s.currentInvitation.craFinalRecommendation;
            s.currentInvitation.craFinalRiskLevel      = data.riskLevel      ?? s.currentInvitation.craFinalRiskLevel;
            // baseline riskLevel + 도메인 점수도 함께 반영 (CRA 수동 입력)
            s.currentInvitation.riskLevel        = data.riskLevel        ?? s.currentInvitation.riskLevel;
            s.currentInvitation.scoreEnrollment  = data.scoreEnrollment  ?? s.currentInvitation.scoreEnrollment;
            s.currentInvitation.scoreOperational = data.scoreOperational ?? s.currentInvitation.scoreOperational;
            s.currentInvitation.scoreRegulatory  = data.scoreRegulatory  ?? s.currentInvitation.scoreRegulatory;
            s.currentInvitation.scoreQuality     = data.scoreQuality     ?? s.currentInvitation.scoreQuality;
            s.currentInvitation.scoreFacility    = data.scoreFacility    ?? s.currentInvitation.scoreFacility;
            s.currentInvitation.scoreFinal       = data.scoreFinal       ?? s.currentInvitation.scoreFinal;
          }
        }
      })

      // CRA Comment — patch answer in-place
      .addCase(saveCRAComment.fulfilled, (s, a) => {
        const { invId, questionKey, commentText } = a.payload;
        if (s.currentInvitation?.id === invId) {
          const ans = s.currentInvitation.answers?.find((x) => x.questionKey === questionKey);
          if (ans) ans.commentText = commentText;
        }
      })

      // Audit trail
      .addCase(fetchAuditTrail.fulfilled, (s, a) => { s.auditTrail = a.payload || []; });
  },
});

export const {
  clearCurrentSFQ,
  clearCurrentVersion,
  clearCurrentInvitation,
  patchInvitationComment,
} = feasibilitySlice.actions;
export default feasibilitySlice.reducer;
